// od_renderer.js
// Shared renderer for OpenDraw (Editor-quality content rendering, no UI overlays)
//
// Exports:
// - setRendererPaths({ courtSrc, iconBase, iconRenderScale })
// - ensureAssetsLoaded()
// - renderFrameToCanvas(frame, canvas, opts)
// - frameHasContent(frame)
// - parseOdplayFromPlayDoc(playDoc)
//
// Notes:
// - Supports BOTH curve schemas:
//   A) Organizer legacy: {ax,ay,bx,by,c1x,c1y,c2x,c2y, kind}
//   B) Editor schema:    {type, a:{x,y}, b:{x,y}, c1:{x,y}, c2:{x,y}}  (normalized 0..1)

let COURT_SRC = "/court.svg";
let ICON_BASE = "/icons/";
let ICON_RENDER_SCALE = 2.2;

let COURT_IMG = null;
const ICON_CACHE = new Map();
const TINT_CACHE = new Map(); // key: img.src|#hex -> canvas

function getTintedIconCanvas(img, hex){
  const key = (img?.src || "") + "|" + hex;
  if (TINT_CACHE.has(key)) return TINT_CACHE.get(key);

  const w = img.naturalWidth || 64;
  const h = img.naturalHeight || 64;

  const c = document.createElement("canvas");
  c.width = w; c.height = h;
  const cx = c.getContext("2d");

  cx.clearRect(0,0,w,h);
  cx.drawImage(img, 0, 0, w, h);

  cx.globalCompositeOperation = "source-in";
  cx.fillStyle = hex;
  cx.fillRect(0,0,w,h);
  cx.globalCompositeOperation = "source-over";

  TINT_CACHE.set(key, c);
  return c;
}


export function setRendererPaths({ courtSrc, iconBase, iconRenderScale } = {}) {
  if (courtSrc) COURT_SRC = courtSrc;
  if (iconBase) ICON_BASE = iconBase.endsWith("/") ? iconBase : iconBase + "/";
  if (typeof iconRenderScale === "number" && isFinite(iconRenderScale)) ICON_RENDER_SCALE = iconRenderScale;
}

function loadImg(src) {
  return new Promise((resolve, reject) => {
    const im = new Image();
    im.onload = () => resolve(im);
    im.onerror = reject;
    im.src = src;
  });
}

export async function ensureAssetsLoaded() {
  if (!COURT_IMG) {
    try { COURT_IMG = await loadImg(COURT_SRC); }
    catch { COURT_IMG = null; }
  }
}

async function getIconImg(iconFile) {
  const key = iconFile || "";
  if (!key) return null;
  if (ICON_CACHE.has(key)) return ICON_CACHE.get(key);

  try {
    const img = await loadImg(ICON_BASE + key);
    ICON_CACHE.set(key, img);
    return img;
  } catch {
    ICON_CACHE.set(key, null);
    return null;
  }
}

function clamp(n, a, b) { return Math.max(a, Math.min(b, n)); }
function clamp01(v) { return Math.max(0, Math.min(1, v)); }

// -------------------- Curve utils (supports both schemas) --------------------
function curveNormToPx(curveNorm, w, h, clamp01 = false) {
  if (!curveNorm) return null;

  const c01 = (v) => clamp01 ? clamp(v, 0, 1) : v;
  const conv = (p) => ({ x: c01(p?.x ?? 0) * w, y: c01(p?.y ?? 0) * h });

  // Format B (Editor): {type, a:{x,y}, b:{x,y}, c1:{x,y}, c2:{x,y}}
  if (curveNorm.a && curveNorm.b) {
    const type = curveNorm.type || (curveNorm.c2 ? "cubic" : (curveNorm.c1 ? "quad" : "line"));
    if (type === "line") return { type: "line", a: conv(curveNorm.a), b: conv(curveNorm.b) };
    if (type === "quad") return { type: "quad", a: conv(curveNorm.a), c1: conv(curveNorm.c1), b: conv(curveNorm.b) };
    return { type: "cubic", a: conv(curveNorm.a), c1: conv(curveNorm.c1), c2: conv(curveNorm.c2), b: conv(curveNorm.b) };
  }

  // Format A (Organizer old): {ax,ay,bx,by,c1x,c1y,c2x,c2y, kind}
  const kind = curveNorm.kind || (curveNorm.c2x != null ? "cubic" : (curveNorm.c1x != null ? "quad" : "line"));

  const a = { x: c01(curveNorm.ax ?? 0) * w, y: c01(curveNorm.ay ?? 0) * h };
  const b = { x: c01(curveNorm.bx ?? 0) * w, y: c01(curveNorm.by ?? 0) * h };

  if (kind === "line") return { type: "line", a, b };
  if (kind === "quad") {
    const c1 = { x: c01(curveNorm.c1x ?? curveNorm.cx ?? 0) * w, y: c01(curveNorm.c1y ?? curveNorm.cy ?? 0) * h };
    return { type: "quad", a, c1, b };
  }
  const c1 = { x: c01(curveNorm.c1x ?? curveNorm.cx ?? 0) * w, y: c01(curveNorm.c1y ?? curveNorm.cy ?? 0) * h };
  const c2 = { x: c01(curveNorm.c2x ?? 0) * w, y: c01(curveNorm.c2y ?? 0) * h };
  return { type: "cubic", a, c1, c2, b };
}

function evalQuad(a, c1, b, t) {
  const u = 1 - t;
  return { x: u*u*a.x + 2*u*t*c1.x + t*t*b.x, y: u*u*a.y + 2*u*t*c1.y + t*t*b.y };
}

function evalCubic(a, c1, c2, b, t) {
  const u = 1 - t;
  return {
    x: u*u*u*a.x + 3*u*u*t*c1.x + 3*u*t*t*c2.x + t*t*t*b.x,
    y: u*u*u*a.y + 3*u*u*t*c1.y + 3*u*t*t*c2.y + t*t*t*b.y
  };
}

function sampleCurvePx(curvePx, steps = 24) {
  const pts = [];
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    if (curvePx.type === "line") {
      pts.push({ x: curvePx.a.x + (curvePx.b.x - curvePx.a.x) * t, y: curvePx.a.y + (curvePx.b.y - curvePx.a.y) * t });
    } else if (curvePx.type === "quad") {
      pts.push(evalQuad(curvePx.a, curvePx.c1, curvePx.b, t));
    } else {
      pts.push(evalCubic(curvePx.a, curvePx.c1, curvePx.c2, curvePx.b, t));
    }
  }
  return pts;
}

function resamplePolylineByStep(points, stepPx) {
  if (!points || points.length < 2) return points || [];
  const out = [points[0]];
  let acc = 0;

  for (let i = 1; i < points.length; i++) {
    let p0 = points[i - 1];
    const p1 = points[i];

    let segDx = p1.x - p0.x;
    let segDy = p1.y - p0.y;
    let segLen = Math.hypot(segDx, segDy);
    if (segLen === 0) continue;

    while (acc + segLen >= stepPx) {
      const t = (stepPx - acc) / segLen;
      const np = { x: p0.x + segDx * t, y: p0.y + segDy * t };
      out.push(np);

      p0 = np;
      segDx = p1.x - p0.x;
      segDy = p1.y - p0.y;
      segLen = Math.hypot(segDx, segDy);
      acc = 0;
      if (segLen === 0) break;
    }
    acc += segLen;
  }

  const last = points[points.length - 1];
  const outLast = out[out.length - 1];
  if (!outLast || Math.hypot(last.x - outLast.x, last.y - outLast.y) > 0.01) out.push(last);
  return out;
}

function polylineLengths(points) {
  const cumulative = [0];
  let total = 0;
  for (let i = 1; i < (points?.length || 0); i++) {
    total += Math.hypot(points[i].x - points[i - 1].x, points[i].y - points[i - 1].y);
    cumulative.push(total);
  }
  return { cumulative, total };
}

function pointAtPolylineDistance(points, cumulative, dist) {
  if (!points?.length) return { p: { x: 0, y: 0 }, tangent: { x: 1, y: 0 } };
  if (points.length === 1) return { p: points[0], tangent: { x: 1, y: 0 } };

  const total = cumulative[cumulative.length - 1] || 0;
  const d = clamp(dist, 0, total);

  let i = 1;
  while (i < cumulative.length && cumulative[i] < d) i++;

  const prev = points[Math.max(0, i - 1)];
  const next = points[Math.min(points.length - 1, i)];
  const segStart = cumulative[Math.max(0, i - 1)] || 0;
  const segLen = Math.max(0.0001, (cumulative[i] || segStart) - segStart);
  const t = clamp((d - segStart) / segLen, 0, 1);

  const p = {
    x: prev.x + (next.x - prev.x) * t,
    y: prev.y + (next.y - prev.y) * t
  };
  const len = Math.hypot(next.x - prev.x, next.y - prev.y) || 1;
  return {
    p,
    tangent: {
      x: (next.x - prev.x) / len,
      y: (next.y - prev.y) / len
    }
  };
}

function drawSmoothPolyline(ctx, pts) {
  if (!pts || pts.length === 0) return;
  if (pts.length === 1) { ctx.moveTo(pts[0].x, pts[0].y); return; }
  if (pts.length === 2) { ctx.moveTo(pts[0].x, pts[0].y); ctx.lineTo(pts[1].x, pts[1].y); return; }

  ctx.moveTo(pts[0].x, pts[0].y);
  for (let i = 1; i < pts.length - 1; i++) {
    const p = pts[i];
    const n = pts[i + 1];
    const mx = (p.x + n.x) / 2;
    const my = (p.y + n.y) / 2;
    ctx.quadraticCurveTo(p.x, p.y, mx, my);
  }
  const pPrev = pts[pts.length - 2];
  const pLast = pts[pts.length - 1];
  ctx.quadraticCurveTo(pPrev.x, pPrev.y, pLast.x, pLast.y);
}

// -------------------- Arrow (Editor-like, no UI) --------------------
function arrowEndTangent(curvePx) {
  // returns unit tangent at end and angle
  let dx = 1, dy = 0;
  if (curvePx.type === "line") {
    dx = curvePx.b.x - curvePx.a.x;
    dy = curvePx.b.y - curvePx.a.y;
  } else if (curvePx.type === "quad") {
    dx = 2 * (curvePx.b.x - curvePx.c1.x);
    dy = 2 * (curvePx.b.y - curvePx.c1.y);
  } else {
    dx = 3 * (curvePx.b.x - curvePx.c2.x);
    dy = 3 * (curvePx.b.y - curvePx.c2.y);
  }
  const dLen = Math.hypot(dx, dy) || 1;
  const ux = dx / dLen;
  const uy = dy / dLen;
  const ang = Math.atan2(uy, ux);
  return { ux, uy, ang };
}

function drawArrowHead(ctx, headType, headSize, strokeWidth, zoom, ux, uy, ang, bxCut, byCut, tipBaseX, tipBaseY, color) {
  const headPush = Math.max(1.5, headSize * 0.06);

  const baseX = (headType === "screen") ? bxCut : tipBaseX;
  const baseY = (headType === "screen") ? byCut : tipBaseY;

  const tipX = baseX + ux * headPush;
  const tipY = baseY + uy * headPush;

  ctx.save();
  ctx.translate(tipX, tipY);
  ctx.rotate(ang);
ctx.fillStyle = color || "#000";


  if (headType === "screen") {
    const capLen = headSize * 1;
    const capTh  = Math.max(3.5, strokeWidth * 1.15) * zoom;
    const capX = -capTh * 0.75; // avoids micro-gap

    ctx.beginPath();
    ctx.rect(capX, -capLen / 2, capTh, capLen);
    ctx.fill();
  } else {
    const L = headSize * 1.05;
    const H = headSize * 0.72;

    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(-L, -H / 2);
    ctx.lineTo(-L,  H / 2);
    ctx.closePath();
    ctx.fill();
  }

  ctx.restore();
}

function drawArrowEditorLike(ctx, curvePx, { style="solid", strokeWidth=3, zoom=1, head="arrow", dirAngOverride=null, isExporting=false, color=null } = {}) {

  ctx.save();

  const sw = clamp(Number(strokeWidth ?? 3), 1, 14);
  // The playbook export renders to a large offscreen canvas and is then scaled
  // down into the PDF. Keep tactical lines lighter there, closer to FastDraw.
  const lineZoom = isExporting ? Math.max(1, zoom * 0.74) : zoom;
  const detailZoom = isExporting ? Math.max(1, zoom * 0.76) : zoom;
  const exportMul = isExporting ? 0.98 : 1.0;
  ctx.lineWidth = sw * exportMul * lineZoom;

  ctx.strokeStyle = color || "#000";
  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  // dash pattern (editor-like scaling)
  if (style === "dash") {
    const dash = Math.max(7, Math.round(12 * detailZoom));
    const gap  = Math.max(6, Math.round(10 * detailZoom));
    ctx.setLineDash([dash, gap]);
  } else {
    ctx.setLineDash([]);
  }

  // direction at end
  let ux = 1, uy = 0, ang = 0;
  if (typeof dirAngOverride === "number" && isFinite(dirAngOverride)) {
    ang = dirAngOverride;
    ux = Math.cos(ang);
    uy = Math.sin(ang);
  } else {
    const t = arrowEndTangent(curvePx);
    ux = t.ux; uy = t.uy; ang = t.ang;
  }

  const headBase = isExporting ? 18 : 22;
  const headSize = (headBase + sw * 2) * detailZoom;
  const cut = headSize * 0.98;

  // Endpoint is NECK
  const bx = curvePx.b.x;
  const by = curvePx.b.y;
  const bxCut = bx;
  const byCut = by;

  // Hook segment aligned with end tangent
  const hookLen = Math.max(1, headSize * 0.1);
  const hx = bxCut - ux * hookLen;
  const hy = byCut - uy * hookLen;

  // Tip base (before push)
  const tipBaseX = bx + ux * cut;
  const tipBaseY = by + uy * cut;

  // BODY
  if (style === "dribble") {
    ctx.setLineDash([]);

    let basePts = sampleCurvePx(curvePx, 140);
    if (!basePts.length) { ctx.restore(); return; }
    basePts[basePts.length - 1] = { x: hx, y: hy };

    // total length
    const sampled = polylineLengths(basePts);
    const total = sampled.total;

    const taperPx = Math.max(8, headSize * 0.4);
    const ampBase = isExporting ? 10.8 : 6;
    const amp = Math.max(isExporting ? 11.2 : 5.5, ampBase * detailZoom);
    const zigStep = Math.max(isExporting ? 13.2 : 10.5, (isExporting ? 8.4 : 10) * detailZoom);
    const wavy = [{ ...basePts[0] }];

    for (let dist = zigStep * 0.55, n = 0; dist < total - taperPx * 0.35; dist += zigStep, n++) {
      const { p, tangent } = pointAtPolylineDistance(basePts, sampled.cumulative, dist);
      const nx = -tangent.y;
      const ny = tangent.x;
      const leadTaper = Math.min(1, dist / Math.max(1, zigStep * 1.2));
      const tailTaper = Math.min(1, (total - dist) / Math.max(1, taperPx));
      const wob = amp * (n % 2 === 0 ? 1 : -1) * leadTaper * tailTaper;
      wavy.push({ x: p.x + nx * wob, y: p.y + ny * wob });
    }
    wavy.push({ x: hx, y: hy });

    ctx.beginPath();
    const prevLineWidth = ctx.lineWidth;
    if (isExporting) ctx.lineWidth *= 1.36;
    drawSmoothPolyline(ctx, wavy);
    ctx.stroke();
    ctx.lineWidth = prevLineWidth;
  } else {
    // solid/dash on curve until hook start
    ctx.beginPath();
    ctx.moveTo(curvePx.a.x, curvePx.a.y);
    if (curvePx.type === "line") {
      ctx.lineTo(hx, hy);
    } else if (curvePx.type === "quad") {
      ctx.quadraticCurveTo(curvePx.c1.x, curvePx.c1.y, hx, hy);
    } else {
      ctx.bezierCurveTo(curvePx.c1.x, curvePx.c1.y, curvePx.c2.x, curvePx.c2.y, hx, hy);
    }
    ctx.stroke();
  }

  // HOOK (always solid)
  ctx.save();
  ctx.setLineDash([]);
  ctx.beginPath();
  ctx.moveTo(hx, hy);
  ctx.lineTo(bxCut, byCut);
  ctx.stroke();
  ctx.restore();

  // HEAD
  ctx.setLineDash([]);
  const headType = head === "screen" ? "screen" : "arrow";
drawArrowHead(ctx, headType, headSize, sw, zoom, ux, uy, ang, bxCut, byCut, tipBaseX, tipBaseY, color);

  ctx.restore();
}

// -------------------- Public API --------------------
export function frameHasContent(frame) {
  if (!frame) return false;
  if (Array.isArray(frame.objects) && frame.objects.length) return true;
  if (Array.isArray(frame.players) && frame.players.length) return true;
  if (Array.isArray(frame.icons) && frame.icons.length) return true;
  if (Array.isArray(frame.arrows) && frame.arrows.length) return true;
  if (Array.isArray(frame.lines) && frame.lines.length) return true;
  if (Array.isArray(frame.texts) && frame.texts.length) return true;
  return false;
}

export function parseOdplayFromPlayDoc(play) {
  if (!play) return null;
  if (play.odplay && typeof play.odplay === "object") return play.odplay;
  if (typeof play.odplayJson === "string") {
    try { return JSON.parse(play.odplayJson); } catch { return null; }
  }
  if (typeof play.odplay === "string") {
    try { return JSON.parse(play.odplay); } catch { return null; }
  }
  return null;
}

/**
 * Render ONE frame to canvas.
 * opts:
 * - zoom (default 1)            // affects dash/dribble/head sizing slightly (editor-like)
 * - background ("court"|"white") default "court"
 * - iconScale (default ICON_RENDER_SCALE)
 */
export async function renderFrameToCanvas(frame, canvas, opts = {}) {
  const w = canvas.width;
  const h = canvas.height;
  const ctx = canvas.getContext("2d");

  const zoom = typeof opts.zoom === "number" && isFinite(opts.zoom) ? opts.zoom : 1;
  const iconScale = typeof opts.iconScale === "number" && isFinite(opts.iconScale) ? opts.iconScale : ICON_RENDER_SCALE;

  ctx.clearRect(0, 0, w, h);

  const courtMode = (opts.courtMode === "ob") ? "ob" : "set";
  const courtScale = (typeof opts.courtScale === "number" && isFinite(opts.courtScale))
    ? opts.courtScale
    : (courtMode === "ob" ? 0.83 : 1.0);

  const isExporting = !!opts.isExporting;

  // fondo blanco: en OB queda "aire" visible
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, w, h);

  // cámara OB: translate + scale aplicado a TODO
  if (courtMode === "ob") {
    const s = clamp(courtScale, 0.2, 1.0);
    const tx = (w * (1 - s)) / 2;
    const ty = (h * (1 - s)) / 2;
    ctx.save();
    ctx.translate(tx, ty);
    ctx.scale(s, s);
  }

  // court
  // court (el SVG ya tiene el borde de 2px incluido)
  if (COURT_IMG) {
    ctx.drawImage(COURT_IMG, 0, 0, w, h);
  }
  const objects = Array.isArray(frame?.objects) ? frame.objects : [];
  const clamp01 = (courtMode === "set");

  // arrows
  const arrows = objects.filter(o => o && o.type === "arrow");
  for (const o of arrows) {
    const curveNorm = o.curve || o;
    const curvePx = curveNormToPx(curveNorm, w, h, clamp01);
    if (!curvePx) continue;

    const styleStr = o.style || o.tool || o.kind || "solid";
    const style = (styleStr === "dribble") ? "dribble" : (styleStr === "dash" ? "dash" : "solid");

    const head = (o.head === "screen" || o.headType === "screen" || o.arrowhead === "screen") ? "screen" : "arrow";
    const sw = Number(o.strokeWidth ?? 3);

    const dirAngOverride =
      (typeof o.dirAngOverride === "number" && isFinite(o.dirAngOverride)) ? o.dirAngOverride :
      (typeof o.dirAng === "number" && isFinite(o.dirAng)) ? o.dirAng :
      null;

drawArrowEditorLike(ctx, curvePx, {
  style,
  strokeWidth: isFinite(sw) ? sw : 3,
  zoom,
  head,
  dirAngOverride,
  isExporting,
  color: o.color || null
});

  }

// icons
const icons = objects.filter(o => o && o.type === "icon");
for (const o of icons) {
  const iconFile = o.icon || o.file || o.src || "";
  const img = await getIconImg(iconFile);
  if (!img) continue;

  const nxRaw = (o.x ?? 0);
  const nyRaw = (o.y ?? 0);

  const nx = (courtMode === "set") ? clamp(nxRaw, 0, 1) : nxRaw;
  const ny = (courtMode === "set") ? clamp(nyRaw, 0, 1) : nyRaw;

  const x = nx * w;
  const y = ny * h;

  const iw = (o.w ?? 0.08) * w * iconScale;
  const ih = (o.h ?? 0.08) * h * iconScale;

  const srcImg = (o.color ? getTintedIconCanvas(img, o.color) : img);
ctx.drawImage(srcImg, x - iw / 2, y - ih / 2, iw, ih);

}

// texts
const texts = objects.filter(o => o && o.type === "text");
for (const o of texts) {
  ctx.fillStyle = o.color || "#111";   // ✅ dentro del loop
  let nx = clamp01 ? clamp(o.x ?? 0, 0, 1) : (o.x ?? 0);
  let ny = clamp01 ? clamp(o.y ?? 0, 0, 1) : (o.y ?? 0);

  // ✅ Export: 1px menos hacia el tope (solo en SET)
  if (clamp01 && isExporting) {
    ny = Math.max(ny, 1 / h);
  }

  const x = nx * w;
  const y = ny * h;

  const size = clamp(Number(o.size ?? 18), 10, 60);
  const txt = String(o.text ?? "");
  ctx.font = `700 ${Math.round(size)}px Arial`;
  ctx.textBaseline = "top";
  ctx.fillText(txt, x, y);
}

  if (courtMode === "ob") ctx.restore();
}

